// Landing A/B test — web-only.
//
// EXPERIMENT DESIGN
// =================
// We are NOT testing the landing page itself — both variants see the
// same hero/landing. What we test is the POST-CLICK flow:
//   • Variant A → click "Try Calofay" → full quiz → signup → checkout
//   • Variant B → click "Try Calofay" → direct checkout
//
// Therefore bucketing happens AT THE MOMENT OF CTA CLICK, not on
// landing page load. A visitor who bounces without clicking is not
// in the sample — they never participated in the experiment. This
// makes "unique CTA clicks" ≡ "sample size" per variant, so both
// buckets always receive the SAME number of clicks (±1), and all
// downstream funnel differences (signup rate, purchase rate,
// revenue) are driven PURELY by the post-click experience.
//
// URL overrides for manual preview:
//   https://calofay.app/ar?ab=A   → forces variant A at CTA click
//   https://calofay.app/ar?ab=B   → forces variant B at CTA click
//
// Admin dashboard reads /api/admin/ab-test/stats.

import { isNativeApp } from "@/lib/native";

const VISITOR_KEY = "calofay_ab_visitor_v1";
const VARIANT_KEY = "calofay_ab_variant_v1";

/** Stable UUID per browser. Generated once, persisted forever. */
export function getVisitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() || `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return `v_${Date.now()}`;
  }
}

/** Synchronous read — returns the already-assigned variant, or `null`
 *  if the visitor hasn't clicked a CTA yet (bucketing is deferred to
 *  the first CTA click, not landing page load). Honors a `?ab=A|B`
 *  URL override by returning the forced value immediately. */
export function getVariant() {
  try {
    const params = new URLSearchParams(window.location.search);
    const forced = (params.get("ab") || "").toUpperCase();
    if (forced === "A" || forced === "B") return forced;
    const existing = localStorage.getItem(VARIANT_KEY);
    if (existing === "A" || existing === "B") return existing;
    return null;
  } catch {
    return null;
  }
}

/** Ask the server to atomically bucket this visitor for the first
 *  CTA click. Call this INSIDE the CTA click handler, awaited, so
 *  the visitor is in the sample only if they actually interacted.
 *  Idempotent per visitor_id (sticky forever once assigned). URL
 *  `?ab=A|B` overrides are forwarded so admin preview visits still
 *  create a matching assignment row. */
let _assignPromise = null;
export async function ensureVariant() {
  try {
    const existing = getVariant();
    // Already in localStorage AND not an override-only read — nothing
    // to do, the server already has the assignment row.
    try {
      const stored = localStorage.getItem(VARIANT_KEY);
      if (stored === "A" || stored === "B") return stored;
    } catch {}
    if (_assignPromise) return _assignPromise;
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    if (!BACKEND) return existing || "A"; // dev fallback
    // Forward `?ab=A|B` as `forced_variant` so the server creates
    // the assignment row with the forced choice instead of running
    // the 50/50 balancer.
    const params = new URLSearchParams(window.location.search);
    const forced = (params.get("ab") || "").toUpperCase();
    _assignPromise = (async () => {
      try {
        const body = { visitor_id: getVisitorId() };
        if (forced === "A" || forced === "B") body.forced_variant = forced;
        const r = await fetch(`${BACKEND}/api/ab-test/assign`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await r.json();
        const v = data?.variant === "B" ? "B" : "A";
        localStorage.setItem(VARIANT_KEY, v);
        return v;
      } catch {
        // Network failure — fall back to coin-flip so UX isn't blocked.
        const v = Math.random() < 0.5 ? "A" : "B";
        localStorage.setItem(VARIANT_KEY, v);
        return v;
      } finally {
        _assignPromise = null;
      }
    })();
    return _assignPromise;
  } catch {
    return "A";
  }
}

/** Internal — POST one event; errors swallowed (tracking must never block UX). */
async function sendEvent(eventType, extra = {}) {
  if (isNativeApp()) return; // web-only experiment
  try {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    if (!BACKEND) return;
    await fetch(`${BACKEND}/api/ab-test/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        variant: getVariant() || "A",
        event_type: eventType,
        ...extra,
      }),
      keepalive: true, // let it finish even if the page navigates away
    });
  } catch {}
}

/** Fire ONCE per visitor+variant — the subscribe CTA was tapped. */
export function trackClick() {
  return sendEvent("click");
}

/** Fire when the user successfully creates their account. */
export function trackSignup(userId) {
  return sendEvent("signup", { user_id: userId });
}

/** Fire when Stripe confirms the charge on the success page. */
export function trackPurchase({ userId, revenueUsd, planKey } = {}) {
  return sendEvent("purchase", {
    user_id: userId,
    revenue_usd: revenueUsd,
    plan_key: planKey,
  });
}
